import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Close, Delete } from "@mui/icons-material";
import HttpService from "../../hooks/Https-services";
import { APIRoutes } from "../../utils/constants";
import { toast } from "react-toastify";

interface ShareChannelModalProps {
  open: boolean;
  onClose: () => void;
  channelId: number | string;
  channelName: string;
}

const ShareChannelModal = ({
  open,
  onClose,
  channelId,
  channelName,
}: ShareChannelModalProps) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingShares, setPendingShares] = useState<any[]>([]);
  const [isCancelling, setIsCancelling] = useState<number | null>(null);

  const fetchShares = async () => {
    try {
      const res = await HttpService.get(APIRoutes.GetChannelShares);
      const all = res?.data?.data || [];
      setPendingShares(
        all.filter((s: any) => s.ChannelID === Number(channelId)),
      );
    } catch {
      // silent
    }
  };

  useEffect(() => {
    if (open && channelId) {
      fetchShares();
    }
  }, [open, channelId]);

  const handleShare = async () => {
    if (!email.trim()) return;
    setIsSubmitting(true);
    try {
      await HttpService.post(APIRoutes.ShareChannel, {
        channelId,
        recipientEmail: email.trim(),
      });
      toast.success("Share request sent successfully");
      setEmail("");
      fetchShares();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to send share request",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async (shareId: number) => {
    setIsCancelling(shareId);
    try {
      await HttpService.deleteData(
        APIRoutes.CancelChannelShare.replace(":shareId", String(shareId)),
      );
      toast.success("Share request cancelled");
      fetchShares();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to cancel share request",
      );
    } finally {
      setIsCancelling(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ color: "#1a1a1a" }}>
            Share "{channelName}"
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          color="text.secondary"
          mb={2}
          sx={{ mb: 1, fontStyle: "normal", textTransform: "none" }}
        >
          Recipients must have an active subscription. They will receive
          an independent copy of this channel.
        </Typography>
        <Box display="flex" gap={1} alignItems="flex-start">
          <TextField
            label="Recipient email"
            type="email"
            fullWidth
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleShare();
            }}
          />
          <Button
            variant="contained"
            onClick={handleShare}
            disabled={isSubmitting || !email.trim()}
            sx={{ whiteSpace: "nowrap", minWidth: "100px" }}
          >
            {isSubmitting ? <CircularProgress size={20} /> : "Send Invite"}
          </Button>
        </Box>

        {pendingShares.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ mb: 1, fontStyle: "normal" }}
            >
              Invites
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "14px",
                }}
              >
                <thead>
                  <tr style={{ borderBottom: "2px solid #e0e0e0" }}>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "8px 12px",
                        fontWeight: 600,
                      }}
                    >
                      Recipient
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "8px 12px",
                        fontWeight: 600,
                      }}
                    >
                      Status
                    </th>
                    <th
                      style={{
                        textAlign: "center",
                        padding: "8px 12px",
                        fontWeight: 600,
                      }}
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pendingShares.map((share: any) => (
                    <tr
                      key={share.ID}
                      style={{ borderBottom: "1px solid #f0f0f0" }}
                    >
                      <td style={{ padding: "8px 12px" }}>
                        {share.RecipientEmail}
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        <Chip
                          label={share.Status}
                          size="small"
                          color={
                            share.Status === "accepted"
                              ? "success"
                              : share.Status === "declined"
                                ? "error"
                                : "default"
                          }
                          variant="outlined"
                        />
                      </td>
                      <td style={{ padding: "8px 12px", textAlign: "center" }}>
                        {share.Status === "pending" && (
                          <IconButton
                            size="small"
                            onClick={() => handleCancel(share.ID)}
                            disabled={isCancelling === share.ID}
                          >
                            {isCancelling === share.ID ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Delete fontSize="small" />
                            )}
                          </IconButton>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareChannelModal;
